import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'
import { Users as UsersIcon, Calendar, GitCompare } from 'lucide-react'
import { Skeleton } from '@/common/ui/skeleton'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import ComparisonDateRangeButtons from '@/common/components/ComparisonDateRangeButtons'
import { ComparisonDateRangePicker } from '@/common/components/ComparisonDateRangePicker'
import ActiveUsersGraph from '../ActiveUsersGraph'
import ActiveUsersSummaryCard from '../ActiveUsersSummaryCard'
import HourlyPatternChart from '../HourlyPatternChart'
import DayOfWeekChart from '../DayOfWeekChart'
import ComparisonInsights from '../ComparisonInsights'

import type {
	GetActiveUsersResponse,
	GetActiveUsersSummaryResponse,
} from '@/modules/dashboard/type'

type ActiveUsersListingProps = {
	data?: GetActiveUsersResponse
	comparisonData?: GetActiveUsersResponse
	summaryData?: GetActiveUsersSummaryResponse
	comparisonSummary?: GetActiveUsersSummaryResponse
	isLoading: boolean
	enableComparison?: boolean
	onToggleComparison?: () => void
	compareStartDate?: string
	compareEndDate?: string
	onCompareStartChange?: (date: string) => void
	onCompareEndChange?: (date: string) => void
	compareDateRange?: string
	onCompareDateRangeChange?: (value: string) => void
}

const noop = () => {}

export default function ActiveUsersListing({
	data,
	comparisonData,
	summaryData,
	comparisonSummary,
	isLoading,
	enableComparison = false,
	onToggleComparison,
	compareStartDate = '',
	compareEndDate = '',
	onCompareStartChange = noop,
	onCompareEndChange = noop,
	compareDateRange = '',
	onCompareDateRangeChange = noop,
}: ActiveUsersListingProps) {
	const hasComparisonData =
		summaryData &&
		(summaryData.hourlyPattern?.length > 0 || summaryData.dayOfWeek?.length > 0)

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 p-6 backdrop-blur-sm md:p-8">
				<div className="bg-grid-white/5 absolute inset-0" />
				<div className="relative space-y-6">
					{/* Title */}
					<div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
						<div className="flex items-center gap-3">
							<div className="rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 p-3 shadow-lg">
								<UsersIcon className="h-6 w-6 text-white md:h-8 md:w-8" />
							</div>
							<div>
								<h1 className="text-2xl font-bold tracking-tight md:text-3xl">
									ผู้ใช้งานออนไลน์
								</h1>
								<p className="text-xs text-muted-foreground md:text-sm">
									สถิติจำนวนผู้ใช้งานที่มีกิจกรรมในระบบตามเวลาจริง
								</p>
							</div>
						</div>

						{/* Comparison Toggle */}
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant={enableComparison ? 'default' : 'outline'}
										size="sm"
										onClick={onToggleComparison}
										className="gap-2"
									>
										<GitCompare className="h-4 w-4" />
										<span>เปรียบเทียบ</span>
										{enableComparison && (
											<Badge
												variant="secondary"
												className="ml-1 h-4 px-1 text-[10px]"
											>
												ON
											</Badge>
										)}
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									<p>
										{enableComparison
											? 'ปิดการเปรียบเทียบ'
											: 'เปิดการเปรียบเทียบ'}
									</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					</div>

					{/* Date Filters */}
					<div className="space-y-3">
						{/* Main Date */}
						<div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
							<div className="flex items-center gap-2 text-sm font-medium">
								<Calendar className="h-4 w-4" />
								<span>ช่วงเวลาหลัก:</span>
							</div>
							<div className="flex flex-wrap gap-2">
								<DateRangeButtons />
								<DateRangePicker />
							</div>
						</div>

						{/* Comparison Date */}
						{enableComparison && (
							<div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
								<div className="flex items-center gap-2 text-sm font-medium text-orange-600 dark:text-orange-400">
									<GitCompare className="h-4 w-4" />
									<span>ช่วงเวลาเปรียบเทียบ:</span>
								</div>
								<div className="flex flex-wrap gap-2">
									<ComparisonDateRangeButtons
										selectedRange={compareDateRange}
										onRangeChange={onCompareDateRangeChange}
										onStartDateChange={onCompareStartChange}
										onEndDateChange={onCompareEndChange}
									/>
									<ComparisonDateRangePicker
										startDate={compareStartDate}
										endDate={compareEndDate}
										onStartDateChange={onCompareStartChange}
										onEndDateChange={onCompareEndChange}
										onRangeChange={onCompareDateRangeChange}
									/>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Decorative */}
				<div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 opacity-20 blur-3xl" />
				<div className="absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 opacity-20 blur-3xl" />
			</div>

			{/* Content */}
			{isLoading ? (
				<div className="space-y-6">
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
						<Skeleton className="h-36 rounded-xl md:h-40" />
						<Skeleton className="h-36 rounded-xl md:h-40" />
						<Skeleton className="h-36 rounded-xl md:h-40" />
					</div>
					<Skeleton className="h-[400px] rounded-xl md:h-[500px]" />
					<div className="grid gap-6 lg:grid-cols-2">
						<Skeleton className="h-[400px] rounded-xl" />
						<Skeleton className="h-[400px] rounded-xl" />
					</div>
				</div>
			) : (
				<div className="space-y-6">
					<ActiveUsersSummaryCard
						data={summaryData}
						comparisonData={comparisonSummary}
						enableComparison={enableComparison}
					/>

					<ActiveUsersGraph
						data={data}
						comparisonData={comparisonData}
						enableComparison={enableComparison}
					/>

					{hasComparisonData && (
						<div className="space-y-6">
							<div className="flex items-center gap-2">
								<div className="h-px flex-1 bg-border" />
								<h2 className="text-sm font-semibold text-muted-foreground">
									การเปรียบเทียบข้อมูล
								</h2>
								<div className="h-px flex-1 bg-border" />
							</div>

							<ComparisonInsights
								data={summaryData}
								comparisonData={comparisonSummary}
								enableComparison={enableComparison}
							/>

							<div className="grid gap-6 lg:grid-cols-2">
								<HourlyPatternChart
									data={summaryData}
									comparisonData={comparisonSummary}
									enableComparison={enableComparison}
								/>
								<DayOfWeekChart
									data={summaryData}
									comparisonData={comparisonSummary}
									enableComparison={enableComparison}
								/>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	)
}
